
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { historyService } from "@/lib/historyUtils";

interface PhotoGalleryProps {
  onSelectImage: (imageDataUrl: string) => void;
  onClose: () => void;
}

export function PhotoGallery({ onSelectImage, onClose }: PhotoGalleryProps) {
  const { user } = useAuth();
  const [images, setImages] = useState<{id: string, url: string, timestamp: string}[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadImages() {
      try {
        setLoading(true);
        
        if (user) {
          // Load images from database for authenticated users
          console.log("📸 Loading images from database");
          const history = await historyService.getUserHistory(user.id);
          const imageList = history.map(item => ({
            id: item.id,
            url: item.imageUrl,
            timestamp: item.timestamp
          }));
          setImages(imageList);
        } else {
          // Fallback to localStorage for non-authenticated users
          console.log("📸 Loading images from localStorage");
          const historyJson = localStorage.getItem("createJoyHistory");
          if (historyJson) {
            const history = JSON.parse(historyJson);
            const imageList = history.map((item: any) => ({
              id: item.id,
              url: item.imageUrl,
              timestamp: item.timestamp
            }));
            setImages(imageList);
          }
        }
      } catch (error) {
        console.error("Error loading images:", error);
        
        // Fallback to localStorage even for authenticated users
        if (user) {
          console.log("🔄 Database failed, falling back to localStorage");
          try {
            const historyJson = localStorage.getItem("createJoyHistory");
            if (historyJson) {
              const history = JSON.parse(historyJson);
              const imageList = history.map((item: any) => ({
                id: item.id,
                url: item.imageUrl,
                timestamp: item.timestamp
              }));
              setImages(imageList);
            }
          } catch (fallbackError) {
            console.error("Even localStorage fallback failed:", fallbackError);
          }
        }
      } finally {
        setLoading(false);
      }
    }
    
    loadImages();
  }, [user]);

  const handleSelectImage = (imageUrl: string) => {
    onSelectImage(imageUrl);
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex flex-col animate-in fade-in">
      <div className="p-4 flex items-center justify-between border-b">
        <h2 className="text-lg font-semibold">Recent Photos</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X size={24} />
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : images.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {images.map((image) => (
              <div 
                key={image.id}
                className="aspect-square relative overflow-hidden rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => handleSelectImage(image.url)}
              >
                <img 
                  src={image.url} 
                  alt="Gallery" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <p className="text-xs text-white">
                    {new Date(image.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <ImageIcon size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">No photos yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm">
              Your captured photos will appear here for reuse
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhotoGallery;
